import { useCallback, useEffect, useState } from 'react';
import { NavigationType, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';

export type UrlListValue = string | number;
export type UrlListDefaults = Record<string, UrlListValue>;
export type UrlListAllowed<S extends UrlListDefaults> = { [K in keyof S]?: readonly S[K][] };

const PAGE_KEY = 'page';
/** Marks URL writes made by this hook, so they are not read back as outside navigation. */
const OWN_WRITE = 'urlListState';

/**
 * Reads list state from the URL. Unknown or invalid values fall back to the default,
 * numbers must be positive integers (pages), and `allowed` restricts enum-like keys.
 */
export function readUrlListState<S extends UrlListDefaults>(
  params: URLSearchParams,
  defaults: S,
  allowed: UrlListAllowed<S> = {},
): S {
  const state = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof S & string)[]) {
    const raw = params.get(key);
    if (raw === null || raw === '') continue;
    let value: UrlListValue = raw;
    if (typeof defaults[key] === 'number') {
      const parsed = Number.parseInt(raw, 10);
      if (!Number.isFinite(parsed) || parsed < 1) continue;
      value = parsed;
    }
    const options = allowed[key];
    if (options && !options.includes(value as S[typeof key])) continue;
    state[key] = value as S[typeof key];
  }
  return state;
}

/**
 * Writes list state into a copy of `params`. Defaults are left out so the URL stays short,
 * and params owned by someone else (e.g. a detail deep link) are kept.
 */
export function writeUrlListState<S extends UrlListDefaults>(
  params: URLSearchParams,
  state: S,
  defaults: S,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of Object.keys(defaults)) {
    const value = typeof state[key] === 'string' ? (state[key] as string).trim() : state[key];
    if (value === defaults[key] || value === '') next.delete(key);
    else next.set(key, String(value));
  }
  return next;
}

/**
 * Merges a patch into the list state. Changing any key other than `page` resets `page` to its
 * default, so a filter change never lands on an empty page; a patch that names `page` keeps it.
 */
export function applyUrlListPatch<S extends UrlListDefaults>(prev: S, patch: Partial<S>, defaults: S): S {
  const next = { ...prev, ...patch };
  const touchesFilter = Object.keys(patch).some((key) => key !== PAGE_KEY);
  if (touchesFilter && PAGE_KEY in defaults && !(PAGE_KEY in patch)) {
    next[PAGE_KEY as keyof S] = defaults[PAGE_KEY as keyof S];
  }
  return next;
}

/**
 * Whether a location change is read back into the list state. Own URL writes are skipped, except
 * on Back/Forward: the history entry keeps the own-write mark, but the list may show something else
 * by then (e.g. after a sidebar click reset it), so the URL wins.
 */
export function shouldReadBackUrl(locationState: unknown, navigationType: NavigationType): boolean {
  const ownWrite = (locationState as Record<string, unknown> | null)?.[OWN_WRITE] === true;
  return !ownWrite || navigationType === NavigationType.Pop;
}

function sameState<S extends UrlListDefaults>(a: S, b: S): boolean {
  return Object.keys(a).every((key) => a[key] === b[key]);
}

/**
 * List state (search, filters, tab, sort, page) that survives navigating to a detail page
 * and back, because it lives in the URL.
 *
 * why: state is kept locally and mirrored into the URL instead of reading the URL on every
 * render — React Router applies URL updates in a transition, which makes controlled search
 * inputs lag behind typing. Navigation from outside (a sidebar link to the same route) is
 * read back, so the URL and the list never disagree.
 */
export function useUrlListState<S extends UrlListDefaults>(
  defaults: S,
  allowed?: UrlListAllowed<S>,
): [S, (patch: Partial<S>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [state, setState] = useState<S>(() => readUrlListState(searchParams, defaults, allowed));

  const update = useCallback(
    (patch: Partial<S>) => setState((prev) => applyUrlListPatch(prev, patch, defaults)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- defaults is a literal at the call site; its values never change between renders
    [],
  );

  useEffect(() => {
    const next = writeUrlListState(searchParams, state, defaults);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true, state: { [OWN_WRITE]: true } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only local changes are mirrored; outside navigation is handled below
  }, [state]);

  useEffect(() => {
    if (!shouldReadBackUrl(location.state, navigationType)) return;
    const fromUrl = readUrlListState(searchParams, defaults, allowed);
    setState((prev) => (sameState(prev, fromUrl) ? prev : fromUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per navigation only; reading the URL on every render would undo local changes not yet mirrored
  }, [location.key]);

  return [state, update];
}
